/**
 * RM-451 — the Activity Feed page's own logic: the period it asks for, the
 * filter chips and the search. Labels, groups and counts come from the server
 * (GET /activitylog/view, lib/activity/view.js), the same view the digest
 * emails render, so nothing here names an event.
 */
import moment from 'moment-timezone'

export const TZ = 'America/New_York'

export const RANGES = [
  { key: 'today', label: 'Today so far' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'Last 7 days' },
  { key: 'custom', label: 'Pick dates' },
]

/**
 * The [from, to) instants for a range, in ET days.
 *
 * @param {'today'|'yesterday'|'week'|'custom'} key
 * @param {{ now?: Date, fromDate?: string, toDate?: string }} opts  dates as YYYY-MM-DD (custom)
 * @returns {{ from: string, to: string }|null} ISO strings, or null when the custom dates are incomplete
 */
export function rangePeriod(key, { now = new Date(), fromDate = '', toDate = '' } = {}) {
  const n = moment(now).tz(TZ)
  const today = n.clone().startOf('day')
  if (key === 'yesterday') return { from: today.clone().subtract(1, 'day').toISOString(), to: today.toISOString() }
  if (key === 'week') return { from: today.clone().subtract(6, 'days').toISOString(), to: n.toISOString() }
  if (key === 'custom') {
    if (!fromDate) return null
    const from = moment.tz(fromDate, 'YYYY-MM-DD', TZ)
    const last = moment.tz(toDate || fromDate, 'YYYY-MM-DD', TZ)
    if (!from.isValid() || !last.isValid() || last.isBefore(from)) return null
    const end = last.clone().add(1, 'day')

    return { from: from.toISOString(), to: (end.isAfter(n) ? n : end).toISOString() }
  }

  return { from: today.toISOString(), to: n.toISOString() }
}

/** "Nov 2, 6 AM – Nov 3, 6 AM": how a period opened from a digest link reads. */
export function periodLabel(from, to) {
  const a = moment(from).tz(TZ)
  const b = moment(to).tz(TZ)
  const t = m => (m.minute() ? m.format('h:mm A') : m.format('h A'))
  if (a.isSame(b, 'day')) return `${a.format('MMM D')}, ${t(a)} – ${t(b)}`

  return `${a.format('MMM D')}, ${t(a)} – ${b.format('MMM D')}, ${t(b)}`
}

/** "4m ago", for the hover on a clock time. */
export function relativeTime(iso, now = new Date()) {
  if (!iso) return ''
  const mins = Math.round((now - new Date(iso)) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)

  return `${days}d ago`
}

/** The chips the page opens with: everything the server marks defaultOn. */
export function defaultChips(categories = []) {
  return categories.filter(c => c.defaultOn).map(c => c.key)
}

/** People in a group whose name, email or team matches the search. */
export function matchPeople(people = [], search = '') {
  const q = String(search || '').trim().toLowerCase()
  if (!q) return people

  return people.filter(p => [p.name, p.email, p.meta, p.laterMember].some(v => String(v || '').toLowerCase().includes(q)))
}

/** "13 page views · Membership, Discover +2 more" */
export function pagesLine(pages) {
  if (!pages) return ''
  const n = pages.count === 1 ? '1 page view' : `${pages.count} page views`

  return `${n} · ${pages.names.join(', ')}${pages.more ? ` +${pages.more} more` : ''}`
}

/** The export's URL query for the period and the groups switched on. */
export function exportParams(period, chips) {
  return { from: period.from, to: period.to, categories: chips.join(',') }
}
